import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  History as HistoryIcon,
  Person,
  Search,
  Warning,
  Info,
  CheckCircle,
  Visibility
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { useFeedback } from '../components/EnhancedFeedbackProvider';
import { useDataContext } from '../contexts/DataContext';

const History = () => {
  const navigate = useNavigate();
  const { showError } = useFeedback();
  const { medicalHistory: allHistory, patients, loading } = useDataContext();
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    filterHistory();
  }, [allHistory, searchTerm, categoryFilter, severityFilter]);

  const filterHistory = () => {
    let filtered = allHistory;

    // Filtre par recherche
    if (searchTerm) {
      filtered = filtered.filter(record => {
        const patient = patients.find(p => p.id === record.patientId);
        return patient?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               patient?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               record.description?.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Filtre par catégorie
    if (categoryFilter) {
      filtered = filtered.filter(record => record.history_type === categoryFilter);
    }

    // Filtre par gravité
    if (severityFilter) {
      filtered = filtered.filter(record => record.severity === severityFilter);
    }

    setFilteredHistory(filtered);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'mild':
        return 'success';
      case 'moderate':
        return 'warning';
      case 'severe':
        return 'error';
      case 'critical':
        return 'error';
      default:
        return 'default';
    }
  };

  const getSeverityLabel = (severity) => {
    switch (severity) {
      case 'mild':
        return 'Léger';
      case 'moderate':
        return 'Modéré';
      case 'severe':
        return 'Sévère';
      case 'critical':
        return 'Critique';
      default:
        return severity;
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'mild':
        return <CheckCircle />;
      case 'moderate':
        return <Info />;
      case 'severe':
        return <Warning />;
      case 'critical':
        return <Warning />;
      default:
        return <Info />;
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'allergies':
        return 'Allergies';
      case 'chronic_diseases':
        return 'Maladies chroniques';
      case 'surgeries':
        return 'Chirurgies';
      case 'hospitalizations':
        return 'Hospitalisations';
      case 'family_history':
        return 'Antécédents familiaux';
      case 'medications':
        return 'Médicaments';
      case 'other':
        return 'Autre';
      default:
        return category;
    }
  };

  const handleRowClick = (record) => {
    if (record.patientId) {
      navigate(`/patients/${record.patientId}`);
    }
  };

  const handleViewPatient = (e, record) => {
    e.stopPropagation(); // Empêcher le clic sur la ligne
    if (record.patientId) {
      navigate(`/patients/${record.patientId}`);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  return (
    <MainLayout>
      <Box>
        {/* En-tête */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Antécédents Médicaux
          </Typography>
        </Box>

        {/* Filtres */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <TextField
                placeholder="Rechercher par patient ou description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 300 }}
              />
              
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Catégorie"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="">Toutes</MenuItem>
                  <MenuItem value="allergies">Allergies</MenuItem>
                  <MenuItem value="chronic_diseases">Maladies chroniques</MenuItem>
                  <MenuItem value="surgeries">Chirurgies</MenuItem>
                  <MenuItem value="hospitalizations">Hospitalisations</MenuItem>
                  <MenuItem value="family_history">Antécédents familiaux</MenuItem>
                  <MenuItem value="medications">Médicaments</MenuItem>
                  <MenuItem value="other">Autre</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Gravité</InputLabel>
                <Select
                  value={severityFilter}
                  label="Gravité"
                  onChange={(e) => setSeverityFilter(e.target.value)}
                >
                  <MenuItem value="">Toutes</MenuItem>
                  <MenuItem value="mild">Léger</MenuItem>
                  <MenuItem value="moderate">Modéré</MenuItem>
                  <MenuItem value="severe">Sévère</MenuItem>
                  <MenuItem value="critical">Critique</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </CardContent>
        </Card>

        {/* Indicateur de chargement */}
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" ml={2}>
              Chargement des antécédents...
            </Typography>
          </Box>
        )}

        {/* Tableau des antécédents */}
        {!loading && (
          <Card>
            <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Patient</TableCell>
                  <TableCell>Catégorie</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Gravité</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHistory
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((record) => (
                    <TableRow
                      key={record.id}
                      hover
                      onClick={() => handleRowClick(record)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Person />
                          <Typography variant="body2">
                            {(() => {
                              const patient = patients.find(p => p.id === record.patientId);
                              return patient ? `${patient.firstName} ${patient.lastName}` : 'Patient inconnu';
                            })()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <HistoryIcon />
                          <Typography variant="body2">
                            {getCategoryLabel(record.history_type)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300 }}>
                          {record.description || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(record.record_date)}</TableCell>
                      <TableCell>
                        <Chip
                          icon={getSeverityIcon(record.severity)}
                          label={getSeverityLabel(record.severity)}
                          color={getSeverityColor(record.severity)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={record.status === 'active' ? 'Actif' : 'Inactif'}
                          color={record.status === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => handleViewPatient(e, record)}
                          color="primary"
                          size="small"
                          title="Voir les détails du patient"
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredHistory.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Lignes par page:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
            }
          />
          </Card>
        )}
      </Box>
    </MainLayout>
  );
};

export default History;